<template>
    <div>
        <!-- Dialog for adding a term -->
        <v-dialog v-model="addTermDialog" max-width="500px" role="dialog" aria-modal="true" aria-labelledby="addterm-title" aria-describedby="addterm-desc">
            <v-card class="greenheader" v-focus-trap="addTermDialog" role="document">
                <v-card-title>
                    <span id="addterm-title" class="text-h6">Add New Term</span>
                </v-card-title>

                <v-card-text class="pt-5 pb-0">
                    <p id="addterm-desc" class="sr-only">Enter the term and select how often it should be used.</p>

                    <!-- Term Eingabefeld -->
                    <v-text-field data-focus-first rounded filled v-model="newTerm.keyword" label="Term" placeholder="Enter term" required />

                    <!-- Frequency Auswahl -->
                    <v-select rounded filled v-model="newTerm.avgKeywordFrequency" :items="frequencyOptions" label="How often should the term be used" placeholder="Select frequency" required />
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>

                    <!-- Cancel -->
                    <v-btn
                        text
                        type="button"
                        aria-label="Cancel"
                        @click="
                            addTermDialog = false
                            newTerm = { keyword: '', avgKeywordFrequency: null }
                        ">
                        Cancel
                    </v-btn>

                    <!-- Add (Enter auf Button) -->
                    <v-btn color="primary" text type="button" aria-label="Add term" @click="addNewTerm" @keyup.enter="addNewTerm"> Add </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <div v-if="loading" class="loaderwrapper" role="status" aria-live="polite" aria-atomic="true" :aria-busy="true">
            <v-row class="loaderimg mb-10">
                <img id="loader" src="../../../assets/quaro-q.svg" alt="Loading" />
            </v-row>

            <v-row class="loaderheading">
                <span id="loader-status" style="font-weight: 300; font-size: 16px" class="mx-auto"> {{ statusNotification.message }}... </span>
            </v-row>

            <v-row class="ma-auto">
                <v-col>
                    <div id="loaderdiv">
                        <v-progress-linear
                            rounded
                            :indeterminate="statusNotification.message ? false : true"
                            height="6px"
                            class="ma-auto"
                            color="primary"
                            :value="statusNotification.progress"
                            role="progressbar"
                            aria-label="Loading progress"
                            :aria-describedby="'loader-status'"
                            :aria-valuemin="0"
                            :aria-valuemax="100"
                            :aria-valuenow="statusNotification.message ? statusNotification.progress : undefined"
                            :aria-valuetext="statusNotification.message ? statusNotification.progress + ' percent' : 'Working...'" />
                    </div>
                </v-col>
            </v-row>

            <v-col class="d-flex mt-10" v-if="statusNotification.type == 'error'">
                <v-btn color="primary" class="mx-auto" type="button" aria-label="Retry process" @click="$emit('retryProcess', 3)"> Retry <v-icon aria-hidden="true">mdi-refresh</v-icon> </v-btn>
            </v-col>

            <p class="loadersubtext mt-10">
                We analyze the content of your competitors' websites. What do your competitors write about? With the help of intelligent text processing and a WDF*IDF analysis, we show you the phrases
                you can use if you want to achieve a good position in the Google search results.
            </p>
            <p class="loadersubtext highlighted">Note: This process usually takes a few seconds. You can leave this page and return later!</p>
        </div>

        <div v-else>
            <div v-if="setup">
                <h1 style="font-weight: 300">Select Content Terms</h1>
                <p>
                    We analyzed the content of your competitors' websites. What do your competitors write about? With the help of intelligent text processing and a WDF*IDF analysis, we show you the
                    phrases you can use if you want to achieve a good position in the Google search results. Now, you can select the terms from the WDF*IDF analysis table which you would like to
                    include in your content briefing.
                </p>
            </div>

            <div class="comp-table-area">
                <v-row class="pa-4" role="search" aria-label="Search terms">
                    <span class="my-auto">
                        <v-chip class="white--text" color="darkgreen" small role="status" aria-live="polite" :aria-label="selected.length + ' selected'"> {{ selected.length }} selected </v-chip>
                    </span>
                    <!-- sichtbarer Ergebniszähler -->
                    <span class="my-auto">
                        <v-chip small class="ml-2" aria-hidden="true" v-if="search.length">{{ resultsStatus }}</v-chip>
                    </span>
                    <!-- SR-Hinweis + Live-Region -->
                    <p id="terms-search-hint" class="sr-only">Type to filter terms. Results update as you type.</p>
                    <p id="terms-count" class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ resultsStatus }}</p>
                    <v-spacer></v-spacer>
                    <v-spacer></v-spacer>

                    <v-btn outlined class="mr-5 my-auto" rounded color="primary" type="button" aria-label="Add term" @click="addTermDialog = true">
                        <v-icon size="16px" class="mr-1" aria-hidden="true">mdi-plus</v-icon>
                        Add Term
                    </v-btn>

                    <v-text-field
                        clearable
                        clear-icon="mdi-close"
                        dense
                        rounded
                        filled
                        v-model="search"
                        max-width="300px"
                        prepend-inner-icon="mdi-magnify"
                        label="search keywords..."
                        :aria-label="'Search keywords'"
                        :aria-controls="'cs_comp_table'"
                        :aria-describedby="'terms-search-hint terms-count'"
                        type="search"
                        autocomplete="off"
                        single-line
                        hide-details
                        @click:clear="search = ''"
                        @keydown.esc.stop.prevent="search = ''">
                        <template #prepend-inner>
                            <v-icon aria-hidden="true">mdi-magnify</v-icon>
                        </template>
                    </v-text-field>
                </v-row>

                <v-data-table
                    id="cs_comp_table"
                    ref="termsTable"
                    show-select
                    :search="search"
                    v-model="selected"
                    fixed-header
                    height="600px"
                    :loading="!topKeywords.length"
                    :headers="headers"
                    :items="topKeywords"
                    class="elevation-0 comp-table"
                    @toggle-select-all="selectPage"
                    :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                    :items-per-page="25"
                    item-key="id"
                    :custom-sort="customSort"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    aria-label="Terms table">
                    <template v-slot:header.data-table-select="{ props }">
                        <div
                            class="select-checkbox"
                            role="checkbox"
                            :aria-label="'Select all rows on this page'"
                            :aria-checked="selected.length == 25 ? 'true' : selected.length ? 'mixed' : 'false'"
                            tabindex="0"
                            @click.stop="selectPage(!(props.every && !props.indeterminate))"
                            @keydown.space.prevent="selectPage(!(props.every && !props.indeterminate))"
                            @keydown.enter.prevent="selectPage(!(props.every && !props.indeterminate))">
                            <v-icon aria-hidden="true">
                                {{ selected.length == 25 ? "mdi-checkbox-marked" : selected.length ? "mdi-minus-box" : "mdi-checkbox-blank-outline" }}
                            </v-icon>
                        </div>
                    </template>
                    <!-- header slot -->
                    <template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
                        <span :key="h.value" :style="{ width: (header && header.width) || h.width }" :aria-sort="h.sortable ? ariaSort(header) : null">
                            <v-tooltip v-if="h.tooltip" color="primary" top max-width="250px" open-on-focus>
                                <template #activator="{ on, attrs }">
                                    <!-- Button nur wenn sortable -->
                                    <v-btn v-if="h.sortable" v-bind="attrs" v-on="on" small text class="th-btn th-btn--header mr-1" :aria-describedby="`tt-${h.value}`" type="button">
                                        <span class="th-text">{{ h.text }}</span>
                                        <v-icon x-small aria-hidden="true" class="ml-1">mdi-help</v-icon>
                                    </v-btn>

                                    <!-- Nicht sortable: fokussierbares Span (wie in SelectCompetitors) -->
                                    <span v-else class="th-label mr-1" tabindex="0" v-bind="attrs" v-on="on" :aria-describedby="`tt-${h.value}`">
                                        <span class="th-text">{{ h.text }}</span>
                                        <v-icon x-small aria-hidden="true" class="ml-1">mdi-help</v-icon>
                                    </span>
                                </template>
                                <span :id="`tt-${h.value}`" role="tooltip">{{ h.tooltip }}</span>
                            </v-tooltip>

                            <!-- Ohne Tooltip -->
                            <v-btn v-else-if="h.sortable" small text class="th-btn th-btn--header" type="button">
                                <span class="th-text">{{ h.text }}</span>
                            </v-btn>
                            <span v-else class="th-label">
                                <span class="th-text">{{ h.text }}</span>
                            </span>

                            <!-- Sort-Status nur bei sortable -->
                            <span v-if="h.sortable" class="sr-only" aria-live="polite">
                                <span v-if="ariaSort(header) === 'ascending'"> (sorted ascending)</span>
                                <span v-else-if="ariaSort(header) === 'descending'"> (sorted descending)</span>
                            </span>
                        </span>
                    </template>

                    <!-- Table Body -->
                    <template v-slot:body="{ items }">
                        <tbody v-if="items.length">
                            <tr v-for="(item, itemIndex) in items" :key="itemIndex" :class="item.selected ? 'highlighted' : ''">
                                <td class="selecttd">
                                    <!-- Tastatur-fokussierbare Checkbox -->
                                    <v-icon
                                        v-if="!item.selected"
                                        class="select-checkbox"
                                        role="checkbox"
                                        :aria-checked="false"
                                        :aria-label="'Select ' + (item.keyword || item.name)"
                                        tabindex="0"
                                        @click="selectItem(itemIndex, item)"
                                        @keydown.enter.prevent="selectItem(itemIndex, item)"
                                        @keydown.space.prevent="selectItem(itemIndex, item)">
                                        mdi-checkbox-blank-outline
                                    </v-icon>
                                    <v-icon
                                        v-else
                                        class="select-checkbox"
                                        role="checkbox"
                                        :aria-checked="true"
                                        :aria-label="'Deselect ' + (item.keyword || item.name)"
                                        tabindex="0"
                                        @click="selectItem(itemIndex, item)"
                                        @keydown.enter.prevent="selectItem(itemIndex, item)"
                                        @keydown.space.prevent="selectItem(itemIndex, item)">
                                        mdi-checkbox-marked
                                    </v-icon>
                                </td>

                                <td v-for="(header, headerIndex) in headers" :key="headerIndex" class="table_td">
                                    <!-- keyword -->
                                    <div v-if="header.value === 'keyword'" class="td_keyword">
                                        {{ item.variants ? item.variants.reduce((a, b) => (a.length < b.length ? a : b)) : item.keyword }}
                                    </div>

                                    <!-- relevance -->
                                    <div v-if="header.value === 'relevance'" v-bind="(relevance = getRelevance(item))">
                                        <v-progress-linear
                                            style="border-radius: 12px; font-size: 12px"
                                            class="white--text"
                                            rounded
                                            :value="relevance.value"
                                            color="primary"
                                            height="24"
                                            role="progressbar"
                                            :aria-valuenow="relevance.value"
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                            :aria-label="`Relevance: ${relevance.value} percent`">
                                            {{ relevance.value }}%
                                        </v-progress-linear>
                                    </div>

                                    <!-- url_coverage -->
                                    <div v-if="header.value === 'url_coverage'">
                                        <v-progress-linear
                                            class="white--text"
                                            style="border-radius: 12px; font-size: 12px"
                                            rounded
                                            :value="getPercent(item[header.value])"
                                            color="cyan darken-1"
                                            height="24"
                                            role="progressbar"
                                            :aria-valuenow="getPercent(item[header.value])"
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                            :aria-label="`Document count coverage: ${getPercent(item[header.value])} percent`">
                                            {{ getPercent(item[header.value]) }}%
                                        </v-progress-linear>
                                    </div>
                                    <!-- -------------- -->
                                    <!---- avgKeywordFrequency ----->
                                    <!-- -------------- -->
                                    <div v-if="header.value === 'avgKeywordFrequency'">{{ item[header.value] ? item[header.value].toFixed(0) : 0 }}</div>
                                    <!-- ------- -->
                                    <!---- avg_wdf----->
                                    <!-- ------- -->
                                    <div v-if="header.value === 'avgWDF'">
                                        {{ item[header.value] ? item[header.value].toFixed(3) : 0 }}
                                    </div>
                                    <!-- ------- -->
                                    <!---- avg_wdfidf----->
                                    <!-- ------- -->
                                    <div v-if="header.value === 'avgWDFIDF'">
                                        {{ getWDFIDF(item) ? getWDFIDF(item) : 0 }}
                                    </div>
                                    <!-- ------- -->
                                    <!---- word_count----->
                                    <!-- ------- -->
                                    <div v-if="header.value === 'word_count'">
                                        {{ item.keyword.split(" ").length }}
                                    </div>
                                    <!-- ------ -->
                                    <!---- idf----->
                                    <!-- ------ -->
                                    <div v-if="header.value === 'idf'">
                                        {{ item[header.value] ? item[header.value].idf.toFixed(3) : 0 }}
                                    </div>
                                    <!-- ------ -->
                                    <!---- termplacement----->
                                    <!-- ------ -->
                                    <div v-if="header.value === 'termplacement'">
                                        <v-chip-group>
                                            <v-chip small class="white--text mr-1 my-1" v-for="(placement, index) in item[header.value]" :key="index" :color="colors[placement.tag]">{{
                                                placement.tag
                                            }}</v-chip>
                                        </v-chip-group>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-data-table>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            project: Object,
            loading: Boolean,
            topKeywords: Array,
            statusNotification: Object,
            setup: Boolean
        },
        data() {
            return {
                sortBy: [],
                sortDesc: [],
                addTermDialog: false,
                newTerm: {
                    keyword: "",
                    avgKeywordFrequency: null
                },
                frequencyOptions: Array.from({ length: 100 }, (_, i) => i + 1), // Generiert Werte von 1 bis 100
                progress: 80,
                colors: {
                    h1: "cyan darken-3",
                    h2: "purple darken-3",
                    h3: "lime darken-3",
                    h4: "light-green darken-3",
                    h5: "blue darken-3",
                    h6: "orange darken-3",
                    description: "grey darken-2",
                    title: "grey darken-2"
                },
                search: "",
                headers: [
                    {
                        text: "Term",
                        value: "keyword",
                        sortable: true
                    },
                    // {
                    //     text: "Ø Search Volume",
                    //     value: "avgsv",
                    //     sortable: true
                    // },
                    {
                        text: "Relevance",
                        value: "relevance",
                        sortable: true,
                        width: "150px",
                        tooltip: "A metric that indicates how relevant a term or keyword for the given topics. The relevance is based on the termplacements, the url coverage and the wdf*idf values."
                    },
                    {
                        text: "URL Coverage",
                        value: "url_coverage",
                        sortable: true,
                        width: "150px",
                        tooltip: "This column shows the number of documents where the keyword appears. It can help you understand how many of your competitors are using the same keyword."
                    },
                    // {
                    //     text: "Term Placement",
                    //     value: "termplacement",
                    //     sortable: false,
                    //     tooltip: "This column shows the location of the keyword in the document. It can help you understand where your competitors are placing the keyword on their website."
                    // },
                    {
                        text: "Ø WDF*IDF",
                        value: "avgWDFIDF",
                        sortable: true,
                        tooltip:
                            "The WDF multiplied by Inverse Document Frequency (WDF*IDF) is a value that indicates how important the keyword is to the document. WDFIDF min/max/Ø show the minimum, maximum, and average WDF*IDF values for the keyword across all documents."
                    },
                    {
                        text: "Ø WDF",
                        value: "avgWDF",
                        sortable: true,
                        tooltip:
                            "The Within Document Frequency (WDF) is a weighted value that represents the keyword density within a document. WDF Ø shows average WDF value for the keyword across all documents."
                    },
                    {
                        text: "IDF",
                        value: "idf",
                        sortable: true,
                        tooltip:
                            "The Inverse Document Frequency (IDF) is a value that indicates how important the keyword is across all documents. A high IDF value means that the keyword is rare and appears in fewer documents."
                    },
                    {
                        text: "Ø Keyword Frequency",
                        value: "avgKeywordFrequency",
                        sortable: true,
                        tooltip: "This column shows the average number of times the keyword appears in each document. It can help you understand how frequently your competitors are using the keyword."
                    },
                    {
                        text: "Word Count",
                        value: "word_count",
                        sortable: true,
                        tooltip: "Shows you how many words the term or phrase consists of."
                    }
                ],
                relevanceData: {
                    maxWDF: 0,
                    minWDF: 0,
                    minURLCoverage: 0,
                    maxURLCoverage: 0,
                    minPlacementValue: 0,
                    maxPlacementValue: 0
                }
            }
        },
        watch: {
            statusNotification(newNotification) {
                console.log("statusNotification watched")
                console.log(newNotification)
            },
            async topKeywords(keywords) {
                console.log("keywords watched")
                this.calcRelevanceData(keywords)
            }
        },
        computed: {
            resultsCount() {
                const t = this.$refs.termsTable
                if (t && Array.isArray(t.filteredItems)) return t.filteredItems.length
                const all = this.topKeywords || []
                const q = (this.search || "").trim().toLowerCase()
                if (!q) return all.length
                return all.filter((it) => {
                    const term = (it.keyword || "").toLowerCase()
                    const vars = Array.isArray(it.variants) ? it.variants.join(" ").toLowerCase() : ""
                    return term.includes(q) || vars.includes(q)
                }).length
            },
            // NEU: Ansagetext
            resultsStatus() {
                const n = this.resultsCount
                if (!this.search) return `${n} term${n === 1 ? "" : "s"}`
                return `${n} result${n === 1 ? "" : "s"} for “${this.search}”`
            },
            selected: {
                get() {
                    let selectedtopKeywords = this.topKeywords.filter((keyword) => keyword.selected)
                    return selectedtopKeywords
                },
                set(val) {
                    console.log("val", val)
                }
            }
        },

        methods: {
            ariaSort(header) {
                if (!header || !header.value) return "none"
                const by = this.sortBy && this.sortBy[0]
                if (by !== header.value) return "none"
                const desc = this.sortDesc && this.sortDesc[0]
                return desc ? "descending" : "ascending"
            },
            async addNewTerm() {
                console.log("addNewTerm")
                console.log(this.newTerm)

                if (this.newTerm.keyword && this.newTerm.avgKeywordFrequency) {
                    let term = this.newTerm.keyword.toLowerCase()
                    let targetedWDFData = this.topKeywords[0].targetedWDF
                    const url = process.env.VUE_APP_APIURL + "/cs/stemm/keyword"
                    let post_data = {
                        company_id: localStorage.company_id,
                        keyword: term,
                        document_language: this.project.langloc.language_code
                    }
                    let response_data = await this.$helpers.axiosPost(url, post_data)
                    console.log(response_data.data)

                    const newKeyword = {
                        keyword: response_data.data,
                        name: term,
                        variants: [term],
                        avgKeywordFrequency: 0,
                        selected: true,
                        avgWDF: 0,
                        targetedWDF: []
                    }
                    if (targetedWDFData) {
                        newKeyword.targetedWDF = [...targetedWDFData]
                    } else {
                        newKeyword.targetedWDF = [
                            {
                                wordCount: this.project.settings.word_count,
                                wordDocumentFreq: 0,
                                avg_wdf: 0
                            },
                            {
                                wordCount: 0,
                                wordDocumentFreq: 0,
                                avg_wdf: 0
                            }
                        ]
                    }
                    newKeyword.targetedWDF[0].wordDocumentFreq = this.newTerm.avgKeywordFrequency
                    newKeyword.avgWDF = Math.log2(newKeyword.targetedWDF[0].wordDocumentFreq - 1) / Math.log2(newKeyword.targetedWDF[0].wordCount)
                    newKeyword.targetedWDF[0].avg_wdf = newKeyword.avgWDF
                    newKeyword.targetedWDF[1].avg_wdf = newKeyword.avgWDF
                    const existingKeyword = this.topKeywords.find((keyword) => keyword.name === newKeyword.name)
                    if (existingKeyword) {
                        existingKeyword.selected = true
                        // move existing keyword to the top of the list
                        this.topKeywords.splice(this.topKeywords.indexOf(existingKeyword), 1)
                        this.topKeywords.unshift(existingKeyword)
                        this.$emit("updateKeywords", [existingKeyword])
                    } else {
                        this.topKeywords.unshift(newKeyword)
                        this.$emit("updateKeywords", [newKeyword])
                    }

                    this.addTermDialog = false
                    this.newTerm = { keyword: "", avgKeywordFrequency: null }
                } else {
                    this.addTermDialog = false
                    this.newTerm = { keyword: "", avgKeywordFrequency: null }
                }
            },
            getWDFIDF(item) {
                return item.avgWDF && item.idf ? (item.avgWDF * item.idf.idf).toFixed(3) : "-"
            },
            customSort(items, index, isDesc) {
                let sortedItems = this.$helpers.globalSort(items, index, isDesc, this.headers, this.relevanceData)
                console.log(sortedItems)
                return sortedItems
            },
            async calcRelevanceData(keywords) {
                // Get min and max Value of all Keywords for normalizing
                this.relevanceData = await this.$helpers.getRelevanceMinMaxValues(keywords)
            },
            getRelevance(item) {
                if (!item.url_coverage) {
                    return { value: 0 }
                }
                const urlCovWeight = 0.15
                const wdfWeight = 0.6
                const termWeight = 0.25
                let normURLCov = item.url_coverage
                let normTerm = (item.termplacementValue - this.relevanceData.minPlacementValue) / (this.relevanceData.maxPlacementValue - this.relevanceData.minPlacementValue)
                normTerm = normTerm > 0 ? Math.min(...[normTerm + 0.52, 1]) : 0 // every keyword with any placement starts at 52 percent

                let normWDF = (item.avgWDF - this.relevanceData.minWDF) / (this.relevanceData.maxWDF - this.relevanceData.minWDF)

                let sum = normURLCov * urlCovWeight + normTerm * termWeight + normWDF * wdfWeight
                return { value: Math.ceil(sum * 100) }
            },
            getPercent(value) {
                if (!value) {
                    return 0
                }
                return parseInt(value * 100)
            },
            selectItem(index, item) {
                let visibleIndex = this.topKeywords.indexOf(item)
                if (!this.topKeywords[visibleIndex].selected) {
                    this.topKeywords[visibleIndex].selected = false
                }
                this.topKeywords[visibleIndex].selected = !this.topKeywords[visibleIndex].selected
                this.$emit("updateKeywords", [item])
            },
            getCurrentPageItems() {
                const table = this.$refs.termsTable
                const filtered = table && table.filteredItems ? table.filteredItems : this.topKeywords || []
                const opt = (table && (table.options || table.internalOptions)) || {}
                const page = opt.page || 1
                const perPage = opt.itemsPerPage || 25
                const start = (page - 1) * perPage
                return filtered.slice(start, start + perPage)
            },
            selectPage(evtOrBool) {
                // Unterstützt sowohl das Vuetify-Event { items, value } als auch boolean (aus unserem Header-Button)
                const isObj = typeof evtOrBool === "object" && evtOrBool !== null
                const value = isObj && "value" in evtOrBool ? !!evtOrBool.value : !!evtOrBool
                const items = isObj && Array.isArray(evtOrBool.items) ? evtOrBool.items : this.getCurrentPageItems()

                const alreadySelected = items.filter((x) => x.selected)
                const shouldSelect = value && alreadySelected.length === 0

                const kwToUpdate = []
                items.forEach((item) => {
                    const kw = this.topKeywords.find((k) => k.keyword === item.keyword)
                    if (kw) {
                        kw.selected = shouldSelect
                        kwToUpdate.push(kw)
                    }
                })
                this.$emit("updateKeywords", kwToUpdate)
            }
        },
        mounted() {
            if (this.topKeywords && this.topKeywords.length) {
                this.calcRelevanceData(this.topKeywords)
            }
        }
    }
</script>

<style scoped>
    .loaderwrapper {
        padding: 50px;
    }

    .loaderimg {
        margin: auto;
    }
    #loader {
        margin: auto;
    }
    .comp-table-area {
        /* background: #f8f8f8 !important; */
        border-radius: 5px;
    }
    .td_keyword {
        font-size: 16px;
        /* font-weight: bold; */
    }
    .comp-table {
    }
    .select-checkbox {
        cursor: pointer;
        border-radius: 4px;
        /* für Outline-Kontur */
        outline: none; /* Basis: keine Default-Outline */
    }
    .select-checkbox:focus-visible {
        outline: 2px solid var(--v-primary-base);
        outline-offset: 3px;
    }
</style>
